<template>
    <v-layout justify-center class="pa-4">
        <v-responsive width="100%" height="100%">
            <list-heading title="회원 API 키" v-model="showsSearch" showsFilterButton />

            <list-search-default
                v-bind="{
                    showsSearch,
                    loading,
                    searchKeys,
                }"
                v-on="{ search }"
            />

            <v-row class="ma-n2 mt-n1">
                <v-col class="pa-2" cols="auto" md="auto">
                    <list-summary v-bind="{ summary }" />
                </v-col>
                <v-col class="pa-2" cols="12" md="auto">
                    <v-card outlined>
                        <v-select
                            :value="isExpired"
                            :items="expiredItems"
                            height="40"
                            dense
                            outlined
                            hide-details
                            class="rounded-0"
                            style="margin: -1px"
                            @change="(isExpired) => $router.push({ query: { ...$route.query, isExpired, page: 1 } })"
                        />
                    </v-card>
                </v-col>
                <v-spacer />
                <v-col class="pa-2" cols="auto" md="auto">
                    <v-card outlined style="overflow: hidden;">
                        <v-btn height="40" color="white" elevation="0" class="text-caption primary--text" style="margin:-1px" v-bind="{ loading }" @click="issueDialog = true">
                            <v-icon>mdi-key-plus</v-icon>
                            <span>키 발급</span>
                        </v-btn>
                    </v-card>
                </v-col>
                <v-col class="pa-2" cols="12">
                    <v-data-table v-bind="{ loading, headers, items }" disable-sort disable-pagination hide-default-footer class="v-sheet--outlined" :items-per-page="-1">
                        <template v-for="(header, index) in headers.filter((header) => header.hasOwnProperty('formatter'))" #[`item.${header.value}`]="{ value, item }"> <span :key="index" v-html="header.formatter.bind(item)(value, item)" /> </template>

                        <template #[`item.scopes`]="{ item }">
                            <v-chip v-for="scope in item.scopes || ['read']" :key="scope" x-small outlined class="ma-1" :color="scope === 'charge' ? 'red' : 'grey darken-1'">
                                {{ scopeText(scope) }}
                            </v-chip>
                        </template>

                        <template #[`item.status`]="{ item }">
                            <v-chip v-if="isRevoked(item)" x-small color="grey" dark>회수됨</v-chip>
                            <v-chip v-else x-small color="primary" dark>사용중</v-chip>
                        </template>

                        <template #[`item.actions`]="{ item }">
                            <v-btn v-if="!isRevoked(item)" v-bind="{ loading }" small text icon tile block @click="revoke(item)">
                                <v-icon small>mdi-key-remove</v-icon>
                            </v-btn>
                            <span v-else>-</span>
                        </template>

                        <template #footer>
                            <v-divider />
                            <v-pagination :value="page" :length="pageCount" :total-visible="11" color="primary" class="my-2" @input="(page) => $router.push({ query: { ...$route.query, page } })" />
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>

            <!-- 발급 -->
            <v-dialog v-model="issueDialog" max-width="480" persistent>
                <v-card>
                    <v-card-title class="subtitle-1">API 키 발급</v-card-title>
                    <v-divider />
                    <v-card-text class="pt-6">
                        <autocomplete-member v-model="form._member" label="회원" placeholder="아이디 또는 이름으로 검색" persistent-placeholder dense outlined class="mb-4" />
                        <v-text-field v-model="form.name" label="용도" placeholder="예: 바우처팩토리 연동" persistent-placeholder dense outlined hide-details />

                        <div class="mt-6">
                            <p class="mb-1 caption grey--text text--darken-2">권한</p>
                            <v-checkbox v-for="item in scopeItems" :key="item.value" v-model="form.scopes" :value="item.value" :label="item.text" hide-details dense class="mt-0 pt-0">
                                <template #label>
                                    <span class="body-2">{{ item.text }}</span>
                                    <span class="ml-2 caption grey--text">{{ item.desc }}</span>
                                </template>
                            </v-checkbox>
                        </div>

                        <p class="mt-4 mb-0 caption grey--text text--darken-1">
                            발급된 키는 발급 직후 한 번만 보입니다. 창을 닫으면 다시 확인할 수 없으므로 반드시 복사해 두세요.
                        </p>
                    </v-card-text>
                    <v-divider />
                    <v-card-actions>
                        <v-spacer />
                        <v-btn text @click="closeIssue">취소</v-btn>
                        <v-btn color="primary" text :loading="loading" @click="issue">발급</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <!-- 발급 결과 -->
            <v-dialog v-model="resultDialog" max-width="640" persistent>
                <v-card>
                    <v-card-title class="subtitle-1">발급된 키</v-card-title>
                    <v-divider />
                    <v-card-text class="pt-6">
                        <v-alert type="warning" dense text class="mb-4 caption">
                            이 값은 지금만 보입니다. 창을 닫으면 다시 확인할 수 없습니다.
                        </v-alert>
                        <v-textarea :value="issuedKey" readonly rows="2" auto-grow outlined hide-details class="caption" />
                    </v-card-text>
                    <v-divider />
                    <v-card-actions>
                        <v-spacer />
                        <v-btn text @click="copy">복사</v-btn>
                        <v-btn color="primary" text @click="closeResult">확인</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-responsive>
    </v-layout>
</template>

<script>
import api from "@/api";
import { initDataTableHeaders } from "@/assets/lib";

import AutocompleteMember from "@/components/console/dumb/autocompletes/autocomplete-member.vue";
import ListHeading from "@/components/console/dumb/list/list-heading.vue";
import ListSearchDefault from "@/components/console/dumb/list/list-search-default.vue";
import ListSummary from "@/components/console/dumb/list/list-summary.vue";

const searchKeys = [
    { text: "회원아이디", value: "member.username" },
    { text: "회원이름", value: "member.name" },
    { text: "용도", value: "name" },
];

const expiredItems = [
    { text: "사용중", value: "false" },
    { text: "회수됨", value: "true" },
    { text: "전체", value: "all" },
];

// 키 권한. 값은 백엔드 api-keys.controller.js 의 SCOPES 와 같아야 한다 — 미들웨어가
// 이 문자열을 그대로 대조하므로, 어긋나면 발급은 되는데 전부 403 이 된다.
const scopeItems = [
    { text: "조회", value: "read", desc: "회원정보·캐시내역·선물내역" },
    { text: "자동충전", value: "charge", desc: "핀 번호로 캐시 충전 — 돈이 움직입니다" },
];

export default {
    components: {
        AutocompleteMember,
        ListHeading,
        ListSearchDefault,
        ListSummary,
    },
    data: () => ({
        apiKeys: [],

        limit: 20,
        summary: { totalCount: 0 },

        searchKeys,
        expiredItems,
        scopeItems,

        loading: false,
        showsSearch: true,

        issueDialog: false,
        resultDialog: false,
        issuedKey: null,

        form: {
            _member: null,
            name: null,
            // 기본은 조회만. 자동충전은 관리자가 명시적으로 골라야 붙는다.
            scopes: ["read"],
        },
    }),
    computed: {
        headers() {
            return initDataTableHeaders([
                {
                    text: "no",
                    value: "index",
                    width: +70,
                    align: "end",
                    formatterType: "number",
                },
                {
                    text: "회원\nID",
                    value: "member.username",
                    width: 140,
                },
                {
                    text: "회원명",
                    value: "member.name",
                    width: 100,
                },
                {
                    text: "용도",
                    value: "name",
                },
                {
                    // 이 키가 무엇까지 할 수 있는지. 안 보이면 "키를 줬는데 왜 안 되느냐"를
                    // 화면만 봐서는 알 수 없다 — 실제로 그런 일이 있었다 (2026-09-10).
                    text: "권한",
                    value: "scopes",
                    align: "center",
                    width: 140,
                },
                {
                    text: "상태",
                    value: "status",
                    align: "center",
                    width: +90,
                },
                {
                    // formatter 는 item 에 bind 되어 호출되므로 this 로 컴포넌트를 쓸 수 없다.
                    // 발급만 되고 한 번도 안 쓰인 키를 "-" 가 아니라 분명히 드러낸다.
                    text: "마지막\n사용",
                    value: "lastUsedAt",
                    width: 140,
                    formatter: (value) => value?.toDateTime?.() || "사용 안 함",
                },
                {
                    text: "발급일시",
                    value: "createdAt",
                    width: 140,
                    formatterType: "datetime",
                },
                {
                    text: "회수",
                    value: "actions",
                    align: "center",
                    width: +70,
                    class: "pa-0",
                    cellClass: "pa-0",
                },
            ]);
        },
        items() {
            return this.apiKeys.map((item, index) => ({
                ...item,
                index: this.summary.totalCount - index - this.skip,
            }));
        },
        page() {
            return +(this.$route.query.page || "1");
        },
        skip() {
            return (this.page - 1) * this.limit;
        },
        pageCount() {
            return Math.ceil(this.summary.totalCount / this.limit) || 1;
        },
        // 상태 필터. 기본은 사용중인 키만 본다.
        isExpired() {
            return this.$route.query.isExpired || "false";
        },
        params() {
            let { ...query } = this.$route.query;

            delete query.page;

            return { isExpired: "false", ...query };
        },
    },
    watch: {
        params() {
            this.search();
        },
    },
    mounted() {
        this.init();
    },
    methods: {
        async init() {
            this.apiKeys = [];
            this.summary = { totalCount: 0 };
            this.loading = false;

            await this.search();
        },

        // 만료 시각이 지났으면 회수된 키다. 문서를 지우지 않으므로 목록에는 남는다.
        isRevoked(apiKey) {
            return !!apiKey.expiredAt && this.$dayjs(apiKey.expiredAt).isBefore(this.$dayjs());
        },

        async search() {
            if (this.loading) return;
            else this.loading = true;

            try {
                let { skip, limit, params } = this;
                let { summary, apiKeys } = await api.console.user.apiKeys.gets({
                    headers: { skip, limit },
                    params,
                });

                this.summary = summary;
                this.apiKeys = apiKeys;
            } finally {
                this.loading = false;
            }
        },

        // 권한 값을 화면 문구로. 모르는 값이 오면 값 자체를 보여준다 — 조용히 감추면
        // 예전에 발급된 키의 권한을 화면에서 확인할 수 없게 된다.
        scopeText(scope) {
            return scopeItems.find((item) => item.value === scope)?.text || scope;
        },

        closeIssue() {
            this.issueDialog = false;
            this.form = { _member: null, name: null, scopes: ["read"] };
        },

        async issue() {
            if (this.loading) return;

            try {
                if (!this.form._member) throw new Error("회원을 선택해주세요");
                if (!this.form.name) throw new Error("용도를 입력해주세요");
                if (!this.form.scopes?.length) throw new Error("권한을 하나 이상 선택해주세요");

                if (this.form.scopes.includes("charge")) {
                    let go = confirm("자동충전 권한이 포함되어 있습니다.\n이 키를 가진 쪽이 회원의 캐시를 충전할 수 있습니다.\n\n계속하시겠습니까?");
                    if (!go) return;
                }

                this.loading = true;

                let { key } = await api.console.user.apiKeys.post(this.form);

                this.closeIssue();

                // 평문 키는 이 응답에만 있다. 창을 닫으면 다시 꺼낼 수 없다.
                this.issuedKey = key;
                this.resultDialog = true;
            } catch (error) {
                alert(error.response?.data?.message || error.message);
            } finally {
                this.loading = false;
                this.search();
            }
        },

        copy() {
            navigator.clipboard
                .writeText(this.issuedKey)
                .then(() => alert("복사되었습니다"))
                .catch(() => alert("복사에 실패했습니다. 직접 선택해 복사해주세요"));
        },

        closeResult() {
            this.resultDialog = false;
            this.issuedKey = null;
        },

        async revoke(apiKey) {
            let go = confirm(`${apiKey?.member?.username || "회원"}의 "${apiKey.name}" 키를 회수하시겠습니까?\n회수하면 이 키로는 더 이상 조회할 수 없습니다.`);
            if (!go) return;

            if (this.loading) return;
            else this.loading = true;

            try {
                await api.console.user.apiKeys.delete(apiKey);
            } catch (error) {
                alert(error.response?.data?.message || error.message);
            } finally {
                this.loading = false;
                this.search();
            }
        },
    },
};
</script>
